import Big from 'big.js';
import {
  useCallback,
  useState
} from 'react';

import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  SubscriptionGeneralTabDocument,
  SubscriptionGuardDocument,
  SubscriptionPaymentHistoryTabDocument,
  SubscriptionPaymentMethodTabDocument,
  useCheckoutSessionDrawerQuery,
  useCreatePaymentIntentMutation,
  useFinalizePaymentMutation
} from 'generated/graphql';
import { appFormUtil } from 'modules/common';
import { useParams } from 'react-router-dom';
import * as ds from './dataService';



export type SubscribeToProductModalProps = {
  clientSecret: string;
  priceId: string;
  amountUsd: Big;
  unitCount: number;
  handleClose: () => void;
};

export const FC = (props: SubscribeToProductModalProps) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data } = useCheckoutSessionDrawerQuery();

  if (!data || !organizationId || !props.clientSecret || !props.priceId) {
    return null;
  }

  return (
    <Drawer
      open
      anchor='right'
      onClose={props.handleClose}
    >
      <Box sx={{
        minWidth: '33vw',
        padding: '2rem'
      }}
      >
        <Elements
          stripe={loadStripe(data.publishableKey)}
          options={{
            mode: 'subscription',
            setupFutureUsage: 'off_session',
            customerSessionClientSecret: props.clientSecret,
            amount: props.amountUsd.mul(100).toNumber(),
            currency: 'usd'
          }}
        >
          <StripeForm
            priceId={props.priceId}
            onFinalized={props.handleClose}
            organizationId={organizationId}
            amountUsd={props.amountUsd}
            unitCount={props.unitCount}
          />
        </Elements>
      </Box>
    </Drawer>
  );
};



const StripeForm = ({
  priceId, onFinalized, organizationId, amountUsd, unitCount
}: {
  priceId: string;
  onFinalized: () => void;
  organizationId: string;
  amountUsd: Big;
  unitCount: number;
}) => {
  const [
    validatePromoCode,
    validatePromoCodeTuple
  ] = ds.useValidatePromoCode();

  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState: {
      promoCode: '',
      hasCheckedPromoCode: false,
      promoCodeValid: false
    }
  });

  const stripe = useStripe();
  const elements = useElements();
  const [
    createPaymentIntent,
    createPaymentIntentTuple
  ] = useCreatePaymentIntentMutation({
  });
  const [
    finalizePayment,
    finalizePaymentTuple
  ] = useFinalizePaymentMutation({
    // For any snoopers... I know. This is not great. But it was the easiest for now. We can come back to this later.
    refetchQueries: [
      {
        query: SubscriptionGeneralTabDocument,
        variables: {
          organizationId
        }
      },
      {
        query: SubscriptionPaymentMethodTabDocument,
        variables: {
          organizationId
        }
      },
      {
        query: SubscriptionPaymentHistoryTabDocument,
        variables: {
          organizationId
        }
      },
      {
        query: SubscriptionGuardDocument
      }
    ]
  });
  const [
    loading,
    setLoading
  ] = useState(false);



  const handleClickSubscribe = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);

    try {
      const submitResponse = await elements.submit();

      if (submitResponse.error) {
        window.alert(submitResponse.error.message);
        return;
      }

      const result = await createPaymentIntent({
        variables: {
          input: {
            organizationId: organizationId ?? '',
            priceId
          }
        }
      });
      const clientSecret = result.data?.createPaymentIntent?.clientSecret;

      if (!clientSecret) {
        throw new Error('Payment intent failed.');
      }

      const confirmResponse = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        clientSecret,
        confirmParams: {
          return_url: window.location.href
        }
      });

      if (confirmResponse.error?.message) {
        window.alert(confirmResponse.error?.message);
      } else {
        await finalizePayment({
          variables: {
            input: {
              promoteKitReferralId: __getPromoteKitId(),
              organizationId,
              priceId,
              promotionCode: formState.hasCheckedPromoCode && formState.promoCodeValid ? formState.promoCode : undefined
            }
          }
        });
        onFinalized();
      }

    } catch (err) {
      if (err instanceof Error) {
        window.alert(err.message);
      }

    } finally {
      setLoading(false);
    }

  }, [
    stripe,
    elements,
    createPaymentIntent,
    organizationId,
    priceId,
    finalizePayment,
    formState.hasCheckedPromoCode,
    formState.promoCodeValid,
    formState.promoCode,
    onFinalized
  ]);



  const handleClickValidatePromoCode = useCallback(async () => {
    if (formState.promoCode) {
      const result = await validatePromoCode(formState.promoCode);
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          hasCheckedPromoCode: true,
          promoCodeValid: result,
          promoCode: formState.promoCode
        }
      });
    }
  }, [
    dispatch,
    formState.promoCode,
    validatePromoCode
  ]);



  const isLoading = (
    createPaymentIntentTuple.loading
    || finalizePaymentTuple.loading
    || loading
    || validatePromoCodeTuple.loading
  );
  const codeCheckedAndInvalid = formState.hasCheckedPromoCode && !formState.promoCodeValid;
  const codeCheckedAndValid = formState.hasCheckedPromoCode && formState.promoCodeValid;

  const textFieldProps = genFieldProps('promoCode');

  return (
    <Stack gap='1rem'>
      <PaymentElement options={{
        layout: 'tabs'
      }}
      />

      <Divider />

      <Stack
        direction='row'
        gap='1rem'
        alignItems='flex-start'
      >
        <TextField
          fullWidth
          size='small'
          placeholder='Promo code'
          disabled={isLoading}
          {...textFieldProps}
          onChange={(ev) => {
            textFieldProps.onChange(ev);

            if (formState.hasCheckedPromoCode) {
              dispatch({
                type: 'UPDATE',
                payload: {
                  property: 'hasCheckedPromoCode',
                  value: false
                }
              });
            }
          }}

        />

        <Button
          variant='outlined'
          disabled={isLoading}
          onClick={handleClickValidatePromoCode}
        >
          Apply
        </Button>
      </Stack>

      {(codeCheckedAndInvalid || codeCheckedAndValid) && (
        <Typography
          color={(theme) => codeCheckedAndInvalid ? theme.palette.error.main : theme.palette.success.main}
        >
          {codeCheckedAndInvalid ? 'Invalid code.' : (codeCheckedAndValid ? 'Success! This code has been applied.' : '')}
        </Typography>
      )}

      <Button
        sx={{
          mt: '1rem'
        }}
        fullWidth
        variant='contained'
        onClick={handleClickSubscribe}
        disabled={isLoading}
      >
        Pay ${amountUsd.toFixed(2)} for {unitCount} seats
      </Button>

      {codeCheckedAndValid && (
        <Typography
          variant='caption'
          color={(theme) => theme.palette.gray.light30}
        >
          Your coupon will affect the final price.
        </Typography>
      )}
    </Stack>
  );
};



function __getPromoteKitId() {
  if ('promotekit_referral' in window && typeof window.promotekit_referral === 'string') {
    return window.promotekit_referral;
  }

  return undefined;
}
